import { motion } from "framer-motion";
import { MessageCircle } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLang } from "../../lib/i18n";

const IMAGE =
  "https://static.prod-images.emergentagent.com/jobs/08f459a3-06c4-4255-ae2d-9ab88df7762a/images/ec603f42f5c94217641cb69165323f484ed5b6c010352957a04eda1c92c6d9f5.jpeg";

const WHATSAPP_INTL = `https://wa.me/393792908157?text=${encodeURIComponent(
  "Hello Atelier Divine! We are planning our destination wedding in Sicily and would love to book a private appointment at your Atelier in Comiso."
)}`;

const POINTS = [
  {
    title: "Zero Travel Stress",
    text: "Trova, personalizza o noleggia il tuo abito da sposa, sposo e cerimonia uomo direttamente in Atelier al tuo arrivo in Sicilia.",
    textEn: "Find, customise or rent your wedding dress, groom suit and men's ceremony wear directly at the Atelier upon your arrival in Sicily.",
  },
  {
    title: "On-Site Fitting & Professional Pressing",
    text: "Servizio di sartoria interna e stiro professionale a vapore per garantire che il tuo abito sia impeccabile per il giorno delle nozze.",
    textEn: "In-house tailoring and professional steam pressing to ensure your gown is impeccable on your wedding day.",
  },
  {
    title: "Complete Style for Wedding Party",
    text: "Accompagniamo non solo gli sposi, ma anche testimoni e invitati stranieri nella scelta del look perfetto per il dress code del tuo evento.",
    textEn: "We assist not only the couple, but also best men, bridesmaids' partners and international guests in choosing the perfect look for your event's dress code.",
  },
];

export const Destination = () => {
  const { lang, tr } = useLang();
  return (
    <section
      id="destination"
      data-testid="destination-section"
      className="relative bg-ad-black py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <Reveal>
            <div className="group relative overflow-hidden">
              <motion.img
                src={IMAGE}
                alt="Destination Wedding in Sicily — Atelier Divine"
                className="w-full h-[480px] md:h-[680px] object-cover"
                initial={{ scale: 1 }}
                whileHover={{ scale: 1.04 }}
                transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#050505]/80 to-transparent p-6">
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-white/80">
                  {tr(
                    "Val di Noto · Provincia di Ragusa · Sicilia",
                    "Val di Noto · Province of Ragusa · Sicily"
                  )}
                </span>
              </div>
            </div>
          </Reveal>

          {/* Text */}
          <div>
            <Reveal>
              <span
                data-testid="destination-badge"
                className="inline-block border border-gold/50 text-gold font-sans-ui text-[10px] md:text-[11px] uppercase tracking-[0.35em] px-4 py-2"
              >
                Destination Wedding in Sicily
              </span>
              <h2 className="mt-7 font-serif-display font-light text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-tight text-white">
                Your Luxury Bridal
                <span className="block italic text-gold">Experience in Sicily</span>
              </h2>
              <p className="mt-4 font-serif-display italic text-xl md:text-2xl text-white/80">
                {tr(
                  "Sposarsi in Sicilia, senza lo stress del viaggio.",
                  "Getting married in Sicily, without the travel stress."
                )}
              </p>
            </Reveal>

            <Reveal delay={0.1}>
              <p className="mt-7 font-sans-ui font-light text-white/70 leading-relaxed max-w-xl">
                {tr(
                  "Pianifichi il tuo matrimonio in una villa d'epoca, in un feudo o sulla costa della provincia di Ragusa? Dimentica la paura di viaggiare in aereo con abiti ingombranti e delicati.",
                  "Planning your wedding in a historic villa, an ancient estate or along the coast of the Ragusa province? Forget the fear of flying with bulky, delicate gowns."
                )}
              </p>
              <p className="mt-3 font-sans-ui font-light text-white/70 leading-relaxed max-w-xl">
                {tr(
                  "Atelier Divine ti accoglie a Comiso per offrirti un'esperienza sartoriale completa direttamente sul posto.",
                  "Atelier Divine welcomes you in Comiso to offer a complete tailoring experience directly on site."
                )}
              </p>
            </Reveal>

            <div className="mt-9 space-y-6">
              {POINTS.map((p, i) => (
                <Reveal key={p.title} delay={0.15 + i * 0.08} data-testid={`destination-point-${i}`}>
                  <div className="flex items-start gap-4">
                    <span className="text-gold text-lg leading-none pt-1">&#10022;</span>
                    <div>
                      <h3 className="font-serif-display text-xl md:text-2xl text-white font-light">
                        {p.title}
                      </h3>
                      <p className="mt-2 font-sans-ui font-light text-sm text-white/60 leading-relaxed max-w-lg">
                        {lang === "it" ? p.text : p.textEn}
                      </p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            <Reveal delay={0.4}>
              <a
                data-testid="destination-whatsapp-cta"
                href={WHATSAPP_INTL}
                target="_blank"
                rel="noopener noreferrer"
                className="animate-cta-pulse mt-10 inline-flex w-full sm:w-auto justify-center items-center gap-3 bg-gold text-[#050505] font-semibold px-6 sm:px-8 py-5 font-sans-ui text-xs md:text-sm uppercase tracking-[0.18em] md:tracking-[0.22em] text-center ring-2 ring-gold/40 ring-offset-2 ring-offset-[#050505] hover:bg-[#e6d3a3] transition-colors duration-400"
              >
                <MessageCircle size={16} />
                Book Your Private Appointment on WhatsApp
              </a>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
